// Tag Management settings strings — EN + TR.
//
// Wave-2 Day 2. Settings sub-section for tag CRUD (V1 mock; M3 Stage 4
// SPEC.md Faz 4 backend ratify). 469 LOC source file was the worst MIXED
// offender per i18n audit 2026-06-02 (commit ca624ba):
// table chrome EN ("Tags / Color / Label / Scope / Usage") + error states
// TR ("Tag listesi yükleniyor…") + modal mixed ("Edit Tag" / "Vazgeç").
//
// Source: features/settings/TagManagement.tsx.

import type { LocalizedDict } from '../index'

type TagManagementStrings = {
  pageTitle: string
  pageDescription: string
  resetToDefaults: string
  addTag: string
  footerNote: string
  columns: {
    color: string
    label: string
    scope: string
    usage: string
    created: string
    actions: string
  }
  states: {
    loading: string
    loadFailed: string
    empty: string
  }
  scopeOptions: {
    all: { label: string; hint: string }
    admin: { label: string; hint: string }
    seller: { label: string; hint: string }
    supply: { label: string; hint: string }
  }
  ariaLabels: {
    editTag: (label: string) => string
    deleteTag: (label: string) => string
    colorSwatch: (color: string) => string
  }
  modal: {
    editTitle: string
    addTitle: string
    description: string
    labelField: string
    labelPlaceholder: string
    colorField: string
    scopeField: string
    scopeAdminOnly: string
    cancel: string
    save: string
    saving: string
    labelRequired: string
  }
  deleteDialog: {
    title: string
    leadBefore: string
    leadAfter: string
    usageWarning: (count: number) => string
    cancel: string
    confirm: string
    deleting: string
  }
}

export const tagManagementI18n: LocalizedDict<TagManagementStrings> = {
  en: {
    pageTitle: 'Tags',
    pageDescription:
      'Tags help categorize orders for filtering, batching, and ops workflows. Pick a color + scope to control which team sees each tag.',
    resetToDefaults: 'Reset to defaults',
    addTag: 'Add Tag',
    footerNote:
      'Mock CRUD · M3 Stage 4 SPEC.md Faz 4 backend persistence ratify · usage_count display-only (live counter in M3).',
    columns: {
      color: 'Color',
      label: 'Label',
      scope: 'Scope',
      usage: 'Usage',
      created: 'Created',
      actions: 'Actions',
    },
    states: {
      loading: 'Loading tags…',
      loadFailed:
        'Could not load tags. Is the MSW handler `/settings/tags` running?',
      empty: 'No tags defined yet. Use "Add Tag" to get started.',
    },
    scopeOptions: {
      all: {
        label: 'All surfaces',
        hint: 'Visible to Admin + Seller + Supply teams',
      },
      admin: {
        label: 'Admin only',
        hint: 'Ops team operational tag (e.g. Hold for Review)',
      },
      seller: {
        label: 'Seller only',
        hint: 'Seller dashboard tags',
      },
      supply: {
        label: 'Supply only',
        hint: 'Production partner / vendor scope tags',
      },
    },
    ariaLabels: {
      editTag: (label) => `Edit ${label}`,
      deleteTag: (label) => `Delete ${label}`,
      colorSwatch: (color) => `Color ${color}`,
    },
    modal: {
      editTitle: 'Edit Tag',
      addTitle: 'Add Tag',
      description:
        'Set the tag label, color, and visibility scope. Mock record; M3 Stage 4 SPEC.md Faz 4 backend.',
      labelField: 'Label',
      labelPlaceholder: 'e.g. Priority Mail',
      colorField: 'Color',
      scopeField: 'Scope',
      scopeAdminOnly: 'Only admin can change the scope.',
      cancel: 'Cancel',
      save: 'Save',
      saving: 'Saving…',
      labelRequired: 'Label cannot be empty.',
    },
    deleteDialog: {
      title: 'Delete tag?',
      leadBefore: 'You are about to remove the ',
      leadAfter: ' tag.',
      usageWarning: (count) =>
        `This tag is attached to ${count} open orders. Deleting still requires "force".`,
      cancel: 'Cancel',
      confirm: 'Delete',
      deleting: 'Deleting…',
    },
  },
  tr: {
    pageTitle: 'Etiketler',
    pageDescription:
      'Etiketler siparişleri filtreleme, gruplama ve operasyon akışları için sınıflandırır. Renk + kapsam ile her ekibe görünür kümeyi belirleyebilirsin.',
    resetToDefaults: 'Varsayılanlara sıfırla',
    addTag: 'Etiket Ekle',
    footerNote:
      'Mock CRUD · M3 Stage 4 SPEC.md Faz 4 backend kalıcılığı ratify · kullanım sayısı sadece görüntü amaçlı (M3\'te canlı sayaç).',
    columns: {
      color: 'Renk',
      label: 'Etiket',
      scope: 'Kapsam',
      usage: 'Kullanım',
      created: 'Oluşturulma',
      actions: 'Eylemler',
    },
    states: {
      loading: 'Etiketler yükleniyor…',
      loadFailed:
        'Etiket listesi yüklenemedi. MSW handler `/settings/tags` ayağa kalkmış mı?',
      empty: 'Henüz tanımlı etiket yok. "Etiket Ekle" ile başlayabilirsin.',
    },
    scopeOptions: {
      all: {
        label: 'Tüm yüzeyler',
        hint: 'Admin + Seller + Supply ekipleri görebilir',
      },
      admin: {
        label: 'Sadece Admin',
        hint: 'Ops ekibi operasyonel etiketi (örn. Hold for Review)',
      },
      seller: {
        label: 'Sadece Seller',
        hint: 'Seller dashboard etiketleri',
      },
      supply: {
        label: 'Sadece Supply',
        hint: 'Üretim ortağı / tedarikçi kapsam etiketleri',
      },
    },
    ariaLabels: {
      editTag: (label) => `${label} düzenle`,
      deleteTag: (label) => `${label} sil`,
      colorSwatch: (color) => `${color} rengi`,
    },
    modal: {
      editTitle: 'Etiketi Düzenle',
      addTitle: 'Etiket Ekle',
      description:
        'Etiket adı, renk ve görünürlük kapsamını belirle. Mock kayıt; M3 Stage 4 SPEC.md Faz 4 backend.',
      labelField: 'Etiket',
      labelPlaceholder: 'örn. Priority Mail',
      colorField: 'Renk',
      scopeField: 'Kapsam',
      scopeAdminOnly: 'Sadece admin kapsamı değiştirebilir.',
      cancel: 'Vazgeç',
      save: 'Kaydet',
      saving: 'Kaydediliyor…',
      labelRequired: 'Etiket boş olamaz.',
    },
    deleteDialog: {
      title: 'Etiketi sil?',
      leadBefore: '',
      leadAfter: ' etiketini kaldırmak üzeresin.',
      usageWarning: (count) =>
        `Bu etiket ${count} açık siparişe ekli. Yine de silmek "force" gerektirir.`,
      cancel: 'Vazgeç',
      confirm: 'Sil',
      deleting: 'Siliniyor…',
    },
  },
}
